import React from 'react';
import {Plus, X} from 'lucide-react';
import Card from '@/shared/components/Card';
import type {OrderTestsSpecimenType, OrderTestsCollector} from '@/shared/types/api';

interface CollectionDetailsProps {
    specimenTypes: OrderTestsSpecimenType[];
    collectors: OrderTestsCollector[];
    specimenTypeId: number | null;
    additionalSpecimenTypeIds: number[];
    onAddSpecimenType: () => void;
    onRemoveSpecimenType: (index: number) => void;
    onChangeAdditionalSpecimenType: (index: number, value: number) => void;
    collectorId: number | null;
    collectionDate: string;
    collectionTime: string;
    notes: string;
    onChange: (field: string, value: string | number) => void;
}

const CollectionDetails: React.FC<CollectionDetailsProps> = ({
    specimenTypes,
    collectors,
    specimenTypeId,
    additionalSpecimenTypeIds,
    onAddSpecimenType,
    onRemoveSpecimenType,
    onChangeAdditionalSpecimenType,
    collectorId,
    collectionDate,
    collectionTime,
    notes,
    onChange,
}) => {
    // IDs already selected (primary + additional) — used to exclude from dropdowns
    const allSelectedIds = new Set<number>();
    if (specimenTypeId) allSelectedIds.add(specimenTypeId);
    for (const id of additionalSpecimenTypeIds) {
        if (id > 0) allSelectedIds.add(id);
    }

    const allTypesSelected = allSelectedIds.size >= specimenTypes.length;

    return (
        <Card>
            <Card.Header>
                <Card.Title>Collection Details</Card.Title>
            </Card.Header>
            <Card.Content>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {/* Specimen Type — primary */}
                    <div>
                        <label htmlFor="specimenType" className="mb-1 block text-xs font-medium text-gray-700">
                            Specimen Type
                        </label>
                        <div className="flex items-center gap-1.5">
                            <select
                                id="specimenType"
                                value={specimenTypeId ?? ''}
                                onChange={(e) => onChange('specimenTypeId', Number(e.target.value))}
                                className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-mld-primary focus:border-transparent">
                                <option value="">Select specimen type...</option>
                                {specimenTypes.map((st) => (
                                    <option key={st.id} value={st.elisSpecimenTypeId}>
                                        {st.name}
                                    </option>
                                ))}
                            </select>
                            <button
                                type="button"
                                onClick={onAddSpecimenType}
                                disabled={!specimenTypeId || allTypesSelected}
                                title="Add additional specimen type"
                                className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-50 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                                <Plus className="h-4 w-4" />
                            </button>
                        </div>
                    </div>

                    {/* Collector */}
                    <div>
                        <label htmlFor="collector" className="mb-1 block text-xs font-medium text-gray-700">
                            Collector
                        </label>
                        <select
                            id="collector"
                            value={collectorId ?? ''}
                            onChange={(e) => onChange('collectorId', Number(e.target.value))}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-mld-primary focus:border-transparent">
                            <option value="">Select collector...</option>
                            {collectors.map((c) => (
                                <option key={c.id} value={c.elisUserId}>
                                    {c.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Additional specimen type rows — each spans the left column */}
                    {additionalSpecimenTypeIds.map((additionalId, index) => {
                        const availableTypes = specimenTypes.filter((st) => {
                            if (st.elisSpecimenTypeId === additionalId) return true;
                            return !allSelectedIds.has(st.elisSpecimenTypeId);
                        });

                        return (
                            <React.Fragment key={index}>
                                <div>
                                    <label className="mb-1 block text-xs font-medium text-gray-700">
                                        Additional Specimen Type
                                    </label>
                                    <div className="flex items-center gap-1.5">
                                        <select
                                            value={additionalId || ''}
                                            onChange={(e) => onChangeAdditionalSpecimenType(index, Number(e.target.value))}
                                            className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-mld-primary focus:border-transparent">
                                            <option value="">Select specimen type...</option>
                                            {availableTypes.map((st) => (
                                                <option key={st.id} value={st.elisSpecimenTypeId}>
                                                    {st.name}
                                                </option>
                                            ))}
                                        </select>
                                        <button
                                            type="button"
                                            onClick={() => onRemoveSpecimenType(index)}
                                            title="Remove specimen type"
                                            className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-lg border border-gray-300 text-gray-400 hover:bg-mld-danger/5 hover:text-mld-danger hover:border-mld-danger/30 transition-colors">
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                                {/* Empty cell to maintain grid alignment */}
                                <div className="hidden sm:block" />
                            </React.Fragment>
                        );
                    })}

                    {/* Collection Date */}
                    <div>
                        <label htmlFor="collectionDate" className="mb-1 block text-xs font-medium text-gray-700">
                            Collection Date
                        </label>
                        <input
                            id="collectionDate"
                            type="date"
                            value={collectionDate}
                            onChange={(e) => onChange('collectionDate', e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                        />
                    </div>

                    {/* Collection Time */}
                    <div>
                        <label htmlFor="collectionTime" className="mb-1 block text-xs font-medium text-gray-700">
                            Collection Time
                        </label>
                        <input
                            id="collectionTime"
                            type="time"
                            value={collectionTime}
                            onChange={(e) => onChange('collectionTime', e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                        />
                    </div>

                    {/* Notes — full-width row, optional. Sent to ELIS as DoctorOrderNote on submit. */}
                    <div className="sm:col-span-2">
                        <label htmlFor="orderNotes" className="mb-1 block text-xs font-medium text-gray-700">
                            Notes <span className="font-normal text-gray-400">(optional)</span>
                        </label>
                        <textarea
                            id="orderNotes"
                            rows={3}
                            value={notes}
                            onChange={(e) => onChange('notes', e.target.value)}
                            placeholder="Optional clinical or specimen notes for this order"
                            className="w-full resize-y rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                        />
                    </div>
                </div>
            </Card.Content>
        </Card>
    );
};

export default CollectionDetails;
